import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, UserRound, RefreshCcw } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { Disclaimer } from "@/components/site/Disclaimer";
import { ProductCard } from "@/components/site/ProductCard";
import { Skeleton } from "@/components/ui/skeleton";
import { getProduct } from "@/data/products";
import { api } from "@/lib/api";
import { SITE_URL } from "@/config/site";
import { formatDate } from "@/pages/WellnessGuide";

// Minimal, safe markdown-ish renderer (## h2, ### h3, - list, paragraphs). No raw HTML.
export function renderContent(text = "") {
  const blocks = [];
  let list = null;
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (line.startsWith("- ")) {
      list = list || [];
      list.push(line.slice(2));
      return;
    }
    if (list) {
      blocks.push(<ul key={`ul-${i}`}>{list.map((li, j) => <li key={j}>{li}</li>)}</ul>);
      list = null;
    }
    if (!line) return;
    if (line.startsWith("### ")) blocks.push(<h3 key={i}>{line.slice(4)}</h3>);
    else if (line.startsWith("## ")) blocks.push(<h2 key={i}>{line.slice(3)}</h2>);
    else blocks.push(<p key={i}>{line}</p>);
  });
  if (list) blocks.push(<ul key="ul-end">{list.map((li, j) => <li key={j}>{li}</li>)}</ul>);
  return blocks;
}

export const ArticleView = ({ article, preview = false }) => {
  const related = (article.related_products || []).map(getProduct).filter(Boolean);
  return (
    <article className="mx-auto max-w-3xl">
      <h1 className="h-display text-4xl sm:text-5xl">{article.title}</h1>
      <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <UserRound className="h-4 w-4" aria-hidden="true" /> {article.author}
        </span>
        {article.published_at ? (
          <span className="flex items-center gap-1.5">
            <CalendarDays className="h-4 w-4" aria-hidden="true" /> Published {formatDate(article.published_at)}
          </span>
        ) : null}
        {article.last_reviewed_at ? (
          <span className="flex items-center gap-1.5">
            <RefreshCcw className="h-4 w-4" aria-hidden="true" /> Last reviewed {formatDate(article.last_reviewed_at)}
            {article.reviewer ? ` by ${article.reviewer}` : ""}
          </span>
        ) : null}
      </div>
      {article.excerpt ? <p className="lead mt-6">{article.excerpt}</p> : null}
      <div className="prose-uhlumo mt-8">{renderContent(article.content)}</div>
      {article.references?.length ? (
        <section className="mt-12 border-t pt-8" aria-labelledby="refs-heading">
          <h2 id="refs-heading" className="text-xl font-semibold">
            References
          </h2>
          <ol className="mt-4 list-decimal space-y-2 pl-6 text-sm text-muted-foreground">
            {article.references.map((r) => (
              <li key={r} className="break-words">
                {r}
              </li>
            ))}
          </ol>
        </section>
      ) : null}
      <div className="mt-10">
        <Disclaimer />
      </div>
      {related.length && !preview ? (
        <section className="mt-12" aria-labelledby="rel-heading">
          <h2 id="rel-heading" className="text-2xl font-semibold">
            Related products
          </h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {related.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
};

export default function Article() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, article: null });
  useEffect(() => {
    setState({ loading: true, article: null });
    api
      .get(`/articles/${slug}`)
      .then((r) => setState({ loading: false, article: r.data }))
      .catch(() => setState({ loading: false, article: null }));
  }, [slug]);

  if (state.loading)
    return (
      <div className="container-site section">
        <Skeleton className="mx-auto h-12 max-w-3xl" />
        <Skeleton className="mx-auto mt-6 h-64 max-w-3xl" />
      </div>
    );

  if (!state.article)
    return (
      <div className="container-site section text-center" data-testid="article-not-found">
        <Seo title="Article not found | Uhlumo Organics" description="This article is not available." path={`/wellness-guide/${slug}`} noindex />
        <h1 className="h-section">Article not available</h1>
        <p className="lead mt-3">This article may still be in editorial review.</p>
        <Link to="/wellness-guide" className="btn btn-primary mt-6">
          Back to the Wellness Guide
        </Link>
      </div>
    );

  const a = state.article;
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Wellness Guide", path: "/wellness-guide" },
    { name: a.title, path: `/wellness-guide/${a.slug}` },
  ];
  return (
    <>
      <Seo
        title={`${a.title} | Uhlumo Wellness Guide`}
        description={a.excerpt || a.title}
        path={`/wellness-guide/${a.slug}`}
        type="article"
        jsonLd={[
          breadcrumbSchema(crumbs),
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: a.title,
            description: a.excerpt,
            author: { "@type": a.author === "Uhlumo Organics" ? "Organization" : "Person", name: a.author },
            datePublished: a.published_at,
            dateModified: a.last_reviewed_at || a.updated_at,
            publisher: { "@id": `${SITE_URL}/#organization` },
            mainEntityOfPage: `${SITE_URL}/wellness-guide/${a.slug}`,
          },
        ]}
      />
      <div className="container-site section">
        <div className="mx-auto mb-8 max-w-3xl">
          <Breadcrumbs items={crumbs} />
        </div>
        <ArticleView article={a} />
      </div>
    </>
  );
}
